import styled from "@emotion/styled";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useWindowSize } from "@reactuses/core";
import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  ShopZoneRow,
  ShopZoneSkipFilter,
  ShopZoneSkipRow,
  getShopZoneSkips,
  getShopZones,
  readShopZoneError,
} from "../../api/shopZone";
import { PageLayout } from "../../components/PageLayout";
import theme from "../../theme";
import userStorage from "../../utils/userStorage";

/**
 * 안전구역 억제 알람 조회 (/shop-zone/alarms)
 *
 * 수집API서버가 임대사업소 안전구역 안이라서 문자·앱 알람을 보내지 않은 전복·위험운전을
 * tb_alrm_skip_log 에 남긴다. 그 기록을 기간·구역·유형으로 조회하고 지도에 찍는다.
 * 인플랩 관리자(00000 / 2)만 볼 수 있다. API 도 같은 기준으로 막는다.
 */

const { md } = theme.screen;
const PAGE_SIZE = 50;

const TYPE_META: Record<string, { label: string; color: string }> = {
  "2": { label: "차량전복", color: "#d7263d" },
  "3": { label: "위험운전", color: "#f28c28" },
};
const STATUS_LABEL: Record<number, string> = { 0: "미처리", 1: "취소", 2: "해제" };
/** 안전구역 원 색 */
const ZONE_COLOR = "#ffd400";

const pad = (v: number) => String(v).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthsAgo = (m: number) => {
  const e = new Date();
  const s = new Date(e);
  s.setMonth(s.getMonth() - m);
  s.setDate(s.getDate() + 1);
  return [ymd(s), ymd(e)] as [string, string];
};
const PRESETS: { key: string; label: string; range: () => [string, string] }[] = [
  {
    key: "7d",
    label: "최근 7일",
    range: () => {
      const e = new Date();
      const s = new Date(e);
      s.setDate(s.getDate() - 6);
      return [ymd(s), ymd(e)];
    },
  },
  { key: "1m", label: "최근 1개월", range: () => monthsAgo(1) },
  { key: "3m", label: "최근 3개월", range: () => monthsAgo(3) },
  { key: "1y", label: "최근 1년", range: () => monthsAgo(12) },
];

const num = (v: unknown) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};
const fmtInt = (v: unknown) => Math.round(num(v)).toLocaleString();
const esc = (s: unknown) =>
  String(s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string)
  );
const getKakao = (): any => (window as any).kakao;

const statusText = (r: ShopZoneSkipRow) =>
  r.prcs_stts_cd === null || r.prcs_stts_cd === undefined
    ? "-"
    : `${STATUS_LABEL[r.prcs_stts_cd] ?? r.prcs_stts_cd}${r.rmv_rsn ? ` · ${r.rmv_rsn}` : ""}`;

const zoneLabel = (z: ShopZoneRow) =>
  [z.sgg_nm, z.ogdp_nm, z.zone_nm !== z.ogdp_nm ? z.zone_nm : null].filter(Boolean).join(" · ");

// ── 지도: 억제된 이벤트 지점과 걸린 안전구역 원 ──
function SkipMap({
  items,
  selected,
  onSelect,
  height,
}: {
  items: ShopZoneSkipRow[];
  selected: number | null;
  onSelect: (id: number) => void;
  height: number;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [mapType, setMapType] = useState<"ROADMAP" | "HYBRID">("HYBRID");
  const infoRef = useRef<any>(null);
  const shapesRef = useRef<any[]>([]);
  const posRef = useRef<Map<number, any>>(new Map());
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    const kakao = getKakao();
    if (!elRef.current || map) return;
    if (!kakao?.maps) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => setFailed((f) => f || !cancelled), 5000);
    kakao.maps.load(() => {
      if (cancelled || !elRef.current) return;
      window.clearTimeout(timer);
      const m = new kakao.maps.Map(elRef.current, { center: new kakao.maps.LatLng(36.3, 127.9), level: 12 });
      m.addControl(new kakao.maps.ZoomControl(), kakao.maps.ControlPosition.RIGHT);
      infoRef.current = new kakao.maps.InfoWindow({ removable: true, zIndex: 20 });
      setMap(m);
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [map]);

  useEffect(() => {
    const kakao = getKakao();
    if (!map || !kakao) return;
    map.setMapTypeId(kakao.maps.MapTypeId[mapType]);
  }, [map, mapType]);

  // 컨테이너 크기가 바뀌면 다시 계산한다
  useEffect(() => {
    const el = elRef.current;
    if (!map || !el || typeof ResizeObserver === "undefined") return;
    let last = "";
    const ro = new ResizeObserver(() => {
      const key = el.clientWidth + "x" + el.clientHeight;
      if (key === last || !el.clientWidth || !el.clientHeight) return;
      last = key;
      const c = map.getCenter();
      map.relayout();
      map.setCenter(c);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);

  const popup = (r: ShopZoneSkipRow) => {
    const t = TYPE_META[r.ocrn_type_cd];
    return `<div style="padding:8px 10px;font-size:12px;line-height:1.55;min-width:200px;max-width:280px">
      <b style="color:${t?.color ?? "#111"}">${esc(t?.label ?? r.ocrn_type_cd)}</b> · 알람 억제<br>
      발생 ${esc(r.ocrn_dt ?? "-")}<br>
      억제 ${esc(r.skip_dt)}<br>
      차량 <b>${esc(r.vhcl_nm ?? "-")}</b> (${esc(r.dvc_sn ?? "-")})<br>
      소속 ${esc(r.ogdp_nm ?? "-")}<br>
      구역 ${esc(r.zone_nm ?? "-")} · 중심에서 ${esc(r.dist_m ?? "-")}m / 반경 ${esc(r.radius_m ?? "-")}m<br>
      처리 ${esc(statusText(r))}
    </div>`;
  };
  const popupRef = useRef(popup);
  popupRef.current = popup;

  // 지점·구역 다시 그리기
  useEffect(() => {
    const kakao = getKakao();
    if (!map || !kakao) return;
    for (const s of shapesRef.current) s.setMap(null);
    shapesRef.current = [];
    posRef.current = new Map();
    infoRef.current?.close();
    const bounds = new kakao.maps.LatLngBounds();
    let any = false;

    const zones = new Map<number, ShopZoneSkipRow>();
    for (const r of items) if (r.shop_zone_id !== null && !zones.has(r.shop_zone_id)) zones.set(r.shop_zone_id, r);
    zones.forEach((r) => {
      const lat = num(r.zone_lat);
      const lot = num(r.zone_lot);
      if (!lat || !lot) return;
      const c = new kakao.maps.Circle({
        map,
        center: new kakao.maps.LatLng(lat, lot),
        radius: num(r.radius_m) || 50,
        // 위성사진의 흙·논 색에 묻히지 않도록 자연에 드문 밝은 노랑을 쓴다
        strokeWeight: 3,
        strokeColor: ZONE_COLOR,
        strokeOpacity: 1,
        strokeStyle: "shortdash",
        fillColor: ZONE_COLOR,
        fillOpacity: 0.18,
      });
      shapesRef.current.push(c);
      const b = c.getBounds();
      bounds.extend(b.getSouthWest());
      bounds.extend(b.getNorthEast());
      any = true;
    });

    for (const r of items) {
      const lat = num(r.ocrn_lat);
      const lot = num(r.ocrn_lot);
      if (!lat || !lot) continue;
      const pos = new kakao.maps.LatLng(lat, lot);
      const color = TYPE_META[r.ocrn_type_cd]?.color ?? "#374151";
      const dot = document.createElement("div");
      dot.style.cssText =
        `width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;` +
        "box-shadow:0 0 0 1px rgba(0,0,0,.35);cursor:pointer";
      dot.title = `${TYPE_META[r.ocrn_type_cd]?.label ?? ""} ${r.skip_dt}`;
      dot.addEventListener("click", () => onSelectRef.current(r.skip_id));
      const ov = new kakao.maps.CustomOverlay({ map, position: pos, content: dot, xAnchor: 0.5, yAnchor: 0.5, clickable: true, zIndex: 5 });
      shapesRef.current.push(ov);
      posRef.current.set(r.skip_id, pos);
      bounds.extend(pos);
      any = true;
    }

    if (any) {
      map.relayout();
      map.setBounds(bounds, 40, 40, 40, 40);
      if (map.getLevel() < 3) map.setLevel(3);
    }
  }, [map, items]);

  // 표에서 고른 건으로 옮겨 팝업을 띄운다
  useEffect(() => {
    const kakao = getKakao();
    if (!map || !kakao || selected === null) return;
    const r = items.find((x) => x.skip_id === selected);
    const pos = posRef.current.get(selected);
    if (!r || !pos) return;
    if (map.getLevel() > 4) map.setLevel(4);
    map.panTo(pos);
    infoRef.current.setContent(popupRef.current(r));
    infoRef.current.setPosition(pos);
    infoRef.current.open(map);
  }, [map, items, selected]);

  return (
    <MapWrap style={{ height }}>
      <div ref={elRef} className="map" />
      <div className="types">
        <button type="button" aria-pressed={mapType === "ROADMAP"} onClick={() => setMapType("ROADMAP")}>
          일반
        </button>
        <button type="button" aria-pressed={mapType === "HYBRID"} onClick={() => setMapType("HYBRID")}>
          위성
        </button>
      </div>
      <div className="legend">
        <span>
          <i style={{ background: TYPE_META["2"].color }} />
          차량전복
        </span>
        <span>
          <i style={{ background: TYPE_META["3"].color }} />
          위험운전
        </span>
        <span>
          <i className="ring" />
          안전구역 반경
        </span>
      </div>
      {failed && (
        <div className="cover">
          지도를 불러오지 못했다. 카카오 개발자 콘솔에 이 주소({window.location.origin})가 등록돼 있는지 확인한다.
        </div>
      )}
    </MapWrap>
  );
}

export default function ShopZoneAlarms() {
  const { width } = useWindowSize();
  const isMobile = width <= md;
  const user = userStorage.get();
  const isAllAdmin = user?.area === "00000" && user?.grade === 2;

  const [preset, setPreset] = useState("3m");
  const [range, setRange] = useState<[string, string]>(() => PRESETS.find((p) => p.key === "3m")!.range());
  const [zoneId, setZoneId] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);

  const rangeOk =
    /^\d{4}-\d{2}-\d{2}$/.test(range[0]) && /^\d{4}-\d{2}-\d{2}$/.test(range[1]) && range[0] <= range[1];

  // 지도 화면과 같은 캐시를 쓴다
  const zonesQ = useQuery({
    queryKey: ["shopZones"],
    queryFn: getShopZones,
    enabled: isAllAdmin,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const zoneOptions = useMemo(
    () => [...(zonesQ.data ?? [])].sort((a, b) => zoneLabel(a).localeCompare(zoneLabel(b), "ko")),
    [zonesQ.data]
  );

  const filter: ShopZoneSkipFilter = useMemo(
    () => ({
      startDate: range[0],
      endDate: range[1],
      shopZoneId: zoneId ? Number(zoneId) : null,
      ocrnTypeCd: type || null,
      page,
      limit: PAGE_SIZE,
    }),
    [range, zoneId, type, page]
  );
  const q = useQuery({
    queryKey: ["shopZone", "skips", filter],
    queryFn: () => getShopZoneSkips(filter),
    enabled: isAllAdmin && rangeOk,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const data = q.data;
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const s = data?.summary;

  useEffect(() => setSelected(null), [filter]);

  const resetPage = () => setPage(1);

  // 엑셀: 지금 조건의 전체 건을 200건씩 받아 모은다
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const all: ShopZoneSkipRow[] = [];
      for (let p = 1; p <= Math.ceil(total / 200); p++) {
        const r = await getShopZoneSkips({ ...filter, page: p, limit: 200 });
        all.push(...r.items);
      }
      const head = [
        "억제 일시",
        "발생 일시",
        "유형",
        "차량",
        "단말",
        "임대사업소",
        "안전구역",
        "구역 반경(m)",
        "중심까지 거리(m)",
        "발생 위도",
        "발생 경도",
        "처리 상태",
        "해제 사유",
        "발생로그 ID",
      ];
      const rows = all.map((r) => [
        r.skip_dt,
        r.ocrn_dt ?? "",
        TYPE_META[r.ocrn_type_cd]?.label ?? r.ocrn_type_cd,
        r.vhcl_nm ?? "",
        r.dvc_sn ?? "",
        r.ogdp_nm ?? "",
        r.zone_nm ?? "",
        r.radius_m ?? "",
        r.dist_m ?? "",
        num(r.ocrn_lat) || "",
        num(r.ocrn_lot) || "",
        r.prcs_stts_cd === null ? "" : STATUS_LABEL[r.prcs_stts_cd] ?? r.prcs_stts_cd,
        r.rmv_rsn ?? "",
        r.ocrn_id ?? "",
      ]);
      const ws = XLSX.utils.aoa_to_sheet([head, ...rows]);
      ws["!cols"] = head.map((h) => ({ wch: h.includes("일시") ? 20 : h === "해제 사유" || h === "임대사업소" ? 28 : 14 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "억제 알람");
      XLSX.writeFile(wb, `안전구역_억제알람_${filter.startDate}_${filter.endDate}.xlsx`);
    } catch (e) {
      window.alert(`엑셀을 만들지 못했다 — ${readShopZoneError(e).message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (!isAllAdmin) {
    return (
      <PageLayout withBreadcrumb withoutFooter>
        <Denied>인플랩 관리자만 볼 수 있는 화면입니다.</Denied>
      </PageLayout>
    );
  }

  const pageNums = (() => {
    const from = Math.max(1, Math.min(page - 2, pages - 4));
    return Array.from({ length: Math.min(5, pages) }, (_, i) => from + i);
  })();

  return (
    <PageLayout withoutBody={!isMobile} withBreadcrumb>
      <Page>
        <Panel className="filters">
          <Head>
            <h1>안전구역 억제 알람</h1>
            <span className="sub">
              임대사업소 안전구역 반경 안에서 감지돼 문자·앱 알람을 보내지 않은 차량전복·위험운전 기록이다. 감지내역에는 그대로 남는다.
            </span>
          </Head>
          <Filters>
            <div className="seg" role="group" aria-label="기간">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  aria-pressed={preset === p.key}
                  onClick={() => {
                    setPreset(p.key);
                    setRange(p.range());
                    resetPage();
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <label className="dates">
              <input
                type="date"
                value={range[0]}
                max={range[1]}
                onChange={(e) => {
                  setPreset("");
                  setRange([e.target.value, range[1]]);
                  resetPage();
                }}
              />
              <span>~</span>
              <input
                type="date"
                value={range[1]}
                min={range[0]}
                onChange={(e) => {
                  setPreset("");
                  setRange([range[0], e.target.value]);
                  resetPage();
                }}
              />
            </label>
            <select
              value={zoneId}
              onChange={(e) => {
                setZoneId(e.target.value);
                resetPage();
              }}
              aria-label="안전구역"
            >
              <option value="">전체 안전구역</option>
              {zoneOptions.map((z) => (
                <option key={z.shop_zone_id} value={String(z.shop_zone_id)}>
                  {zoneLabel(z)}
                </option>
              ))}
            </select>
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                resetPage();
              }}
              aria-label="유형"
            >
              <option value="">전체 유형</option>
              <option value="2">차량전복</option>
              <option value="3">위험운전</option>
            </select>
            {!rangeOk && <span className="warn">기간을 확인한다</span>}
            <span className="spacer" />
            <button type="button" className="ghost" onClick={() => q.refetch()} disabled={q.isFetching}>
              {q.isFetching ? "불러오는 중…" : "새로고침"}
            </button>
            <button type="button" className="excel" onClick={download} disabled={!total || downloading}>
              {downloading ? "만드는 중…" : "엑셀 다운로드"}
            </button>
          </Filters>
        </Panel>

        {q.error && <Notice className="bad">불러오지 못했다 — {readShopZoneError(q.error).message}</Notice>}

        <Kpis>
          <Kpi>
            <div className="t">억제 알람</div>
            <div className="v">{fmtInt(s?.total)}건</div>
          </Kpi>
          <Kpi>
            <div className="t">차량전복</div>
            <div className="v" style={{ color: TYPE_META["2"].color }}>
              {fmtInt(s?.rollover)}건
            </div>
          </Kpi>
          <Kpi>
            <div className="t">위험운전</div>
            <div className="v" style={{ color: TYPE_META["3"].color }}>
              {fmtInt(s?.danger)}건
            </div>
          </Kpi>
          <Kpi>
            <div className="t">단말</div>
            <div className="v">{fmtInt(s?.devices)}대</div>
          </Kpi>
          <Kpi>
            <div className="t">안전구역</div>
            <div className="v">{fmtInt(s?.zones)}곳</div>
          </Kpi>
        </Kpis>

        <Grid>
          <Panel className="card">
            <CardHead>
              <h2>지도</h2>
              <span className="sub">이 페이지의 {items.length}건 · 점을 누르면 상세가 나온다</span>
            </CardHead>
            <SkipMap items={items} selected={selected} onSelect={setSelected} height={isMobile ? 360 : 560} />
          </Panel>

          <Panel className="card">
            <CardHead>
              <h2>억제 목록</h2>
              <span className="sub">
                {data ? `${data.startDate} ~ ${data.endDate} · 총 ${fmtInt(total)}건` : ""}
                {q.isFetching && " · 불러오는 중…"}
              </span>
            </CardHead>
            {q.isLoading ? (
              <p className="empty">불러오는 중…</p>
            ) : !items.length ? (
              <p className="empty">조건에 맞는 억제 알람이 없다.</p>
            ) : (
              <TableWrap>
                <table>
                  <thead>
                    <tr>
                      <th>억제 일시</th>
                      <th>유형</th>
                      <th>차량</th>
                      <th>안전구역</th>
                      <th title="반경은 지금 설정값이다">중심거리/반경</th>
                      <th>처리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <tr
                        key={r.skip_id}
                        className={selected === r.skip_id ? "on" : undefined}
                        onClick={() => setSelected(r.skip_id)}
                      >
                        <td>
                          {r.skip_dt}
                          {r.ocrn_dt && <div className="s">발생 {r.ocrn_dt.slice(11)}</div>}
                        </td>
                        <td>
                          <span className="tag" style={{ background: TYPE_META[r.ocrn_type_cd]?.color ?? "#6b7280" }}>
                            {TYPE_META[r.ocrn_type_cd]?.label ?? r.ocrn_type_cd}
                          </span>
                        </td>
                        <td className="l">
                          {r.vhcl_nm ?? "-"}
                          <div className="s">{r.dvc_sn ?? "-"}</div>
                        </td>
                        <td className="l">
                          {r.zone_nm ?? "-"}
                          <div className="s">{r.ogdp_nm ?? ""}</div>
                        </td>
                        <td className="r">
                          {r.dist_m ?? "-"}m
                          <div className="s">/ {r.radius_m ?? "-"}m</div>
                        </td>
                        <td className="l wrap">{statusText(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            )}
            {pages > 1 && (
              <Pager>
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  이전
                </button>
                {pageNums.map((p) => (
                  <button key={p} type="button" aria-pressed={p === page} onClick={() => setPage(p)}>
                    {p}
                  </button>
                ))}
                <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                  다음
                </button>
              </Pager>
            )}
          </Panel>
        </Grid>
      </Page>
    </PageLayout>
  );
}

// ── 스타일 ── 통계·빅데이터 분석 화면과 같은 패널 모양
const panelCss = `
  background: ${theme.color.neutral_50};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
  min-width: 0;
  @media (max-width: ${md}px) {
    background: ${theme.color.white};
    box-shadow: none;
    border-radius: 8px;
  }
`;

const Denied = styled.div`
  padding: 80px 20px;
  text-align: center;
  color: ${theme.color.neutral_500};
  font-size: 15px;
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 23px;
  padding-bottom: 20px;
  @media (max-width: ${md}px) {
    gap: 12px;
    padding-bottom: 0;
  }
`;

const Panel = styled.section`
  ${panelCss}
  &.filters {
    border-radius: 10px;
    min-height: 92px;
    padding: 16px 30px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
  }
  &.card {
    border-radius: 15px;
    padding: 20px 24px;
  }
  .empty {
    padding: 40px 0;
    text-align: center;
    color: ${theme.color.neutral_500};
    font-size: 13px;
  }
  @media (max-width: ${md}px) {
    &.filters,
    &.card {
      padding: 14px;
      min-height: 0;
    }
  }
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: ${theme.color.neutral_800};
  }
  .sub {
    font-size: 13px;
    color: ${theme.color.neutral_500};
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  .seg {
    display: inline-flex;
    flex-wrap: wrap;
  }
  .seg button {
    font-size: 13px;
    padding: 6px 12px;
    border: 1px solid ${theme.color.neutral_300};
    background: #fff;
    color: ${theme.color.neutral_700};
    cursor: pointer;
    margin-left: -1px;
  }
  .seg button:first-of-type {
    border-radius: 6px 0 0 6px;
    margin-left: 0;
  }
  .seg button:last-of-type {
    border-radius: 0 6px 6px 0;
  }
  .seg button[aria-pressed="true"] {
    background: ${theme.color.green_500};
    border-color: ${theme.color.green_500};
    color: #fff;
    position: relative;
  }
  .dates {
    display: inline-flex;
    gap: 6px;
    align-items: center;
  }
  input[type="date"],
  select {
    height: 34px;
    border: 1px solid ${theme.color.neutral_300};
    border-radius: 6px;
    padding: 0 8px;
    font-size: 13px;
    color: ${theme.color.neutral_700};
    background: #fff;
  }
  select {
    min-width: 140px;
    max-width: 320px;
    cursor: pointer;
  }
  .warn {
    color: ${theme.color.red_500};
    font-size: 12px;
  }
  .spacer {
    flex: 1 1 auto;
  }
  .ghost,
  .excel {
    height: 34px;
    font-size: 13px;
    padding: 0 14px;
    border-radius: 6px;
    cursor: pointer;
  }
  .ghost {
    border: 1px solid ${theme.color.neutral_300};
    background: #fff;
    color: ${theme.color.neutral_700};
  }
  .excel {
    border: 1px solid ${theme.color.green_500};
    background: ${theme.color.green_500};
    color: #fff;
  }
  .ghost:disabled,
  .excel:disabled {
    opacity: 0.45;
    cursor: default;
  }
  @media (max-width: ${md}px) {
    select {
      flex: 1 1 140px;
      max-width: none;
    }
  }
`;

const Notice = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff;
  color: ${theme.color.neutral_500};
  font-size: 13px;
  &.bad {
    color: ${theme.color.red_600};
  }
`;

const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: ${md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
`;

const Kpi = styled.div`
  ${panelCss}
  border-radius: 15px;
  padding: 16px 20px;
  .t {
    font-size: 12.5px;
    color: ${theme.color.neutral_500};
  }
  .v {
    font-size: 22px;
    font-weight: 700;
    color: ${theme.color.neutral_800};
    margin-top: 4px;
    font-variant-numeric: tabular-nums;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  gap: 23px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
  @media (max-width: ${md}px) {
    gap: 12px;
  }
`;

const CardHead = styled.div`
  display: flex;
  gap: 12px;
  align-items: baseline;
  flex-wrap: wrap;
  margin-bottom: 10px;
  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${theme.color.neutral_800};
  }
  .sub {
    font-size: 12px;
    color: ${theme.color.neutral_500};
  }
`;

const MapWrap = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: #e5e7eb;
  .map {
    position: absolute;
    inset: 0;
  }
  .types {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 3;
    display: inline-flex;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    border-radius: 4px;
  }
  .types button {
    font-size: 12px;
    padding: 4px 10px;
    border: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    cursor: pointer;
    margin-left: -1px;
  }
  .types button:first-of-type {
    border-radius: 4px 0 0 4px;
    margin-left: 0;
  }
  .types button:last-of-type {
    border-radius: 0 4px 4px 0;
  }
  .types button[aria-pressed="true"] {
    background: ${theme.color.green_500};
    border-color: ${theme.color.green_500};
    color: #fff;
  }
  .legend {
    position: absolute;
    left: 10px;
    bottom: 10px;
    z-index: 3;
    display: flex;
    gap: 10px;
    padding: 5px 9px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.92);
    font-size: 11.5px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  .legend span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .legend i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  .legend i.ring {
    border: 2px dashed ${ZONE_COLOR};
    background: rgba(255, 212, 0, 0.18);
    width: 8px;
    height: 8px;
  }
  .cover {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: grid;
    place-content: center;
    padding: 24px;
    text-align: center;
    font-size: 13px;
    color: ${theme.color.neutral_600};
    background: rgba(255, 255, 255, 0.92);
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
  }
  th {
    font-weight: 600;
    color: ${theme.color.neutral_600};
    background: #fefefe;
    padding: 8px 8px;
    border-bottom: 1px solid ${theme.color.neutral_200};
    white-space: nowrap;
  }
  td {
    padding: 8px 8px;
    border-bottom: 1px solid ${theme.color.neutral_100};
    background: #fefefe;
    text-align: center;
    white-space: nowrap;
    vertical-align: top;
  }
  td.l {
    text-align: left;
  }
  td.r {
    text-align: right;
  }
  td.wrap {
    white-space: normal;
    min-width: 120px;
  }
  td .s {
    font-size: 11px;
    color: ${theme.color.neutral_500};
    margin-top: 2px;
  }
  tbody tr {
    cursor: pointer;
  }
  tbody tr:hover td {
    background: #f3f4f6;
  }
  tbody tr.on td {
    background: #ecfdf3;
  }
  .tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    color: #fff;
    font-size: 11.5px;
    font-weight: 600;
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-top: 12px;
  button {
    min-width: 32px;
    height: 30px;
    font-size: 12.5px;
    border: 1px solid ${theme.color.neutral_300};
    border-radius: 6px;
    background: #fff;
    color: ${theme.color.neutral_700};
    cursor: pointer;
  }
  button[aria-pressed="true"] {
    background: ${theme.color.green_500};
    border-color: ${theme.color.green_500};
    color: #fff;
  }
  button:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
